import { DataObject } from "@lib/titan2/dist/api/core/data/DataObject";
import { SolidifyableData } from "@lib/titan2/dist/api/core/data/SolidifyableData";
import { Parser } from "../Parser";
import { Action } from "../model/enum/Action";
import { GameState } from "../model/enum/GameState";
import { SlingoConstants } from "../model/enum/SlingoConstants";
/**
 * ReplayParser.ts
 */
export class ReplayParser extends Parser {
    constructor(data) {
        super();
        //If you want to add more properties to the spin response, you can add property values to these arrays in your extended class
        this.additionalSharedSpinProperties = [];
        this.additionalUniqueSpinProperties = [];
        const filteredSpinResults = data.getDataArray(SlingoConstants.PROPERTY_SPIN_RESULTS)?.filter(spinResult => spinResult.getString(SlingoConstants.PROPERTY_STATE) !== GameState.CHOOSE_CELL.getType()) || [];
        const filteredData = this.cloneData(data);
        filteredData.putDataArray(SlingoConstants.PROPERTY_SPIN_RESULTS, filteredSpinResults);
        const parsedStakeToSpinData = this.parseStakeToSpins(filteredData);
        this.addResponse("CurrentGame", this.updateCurrentGameData(parsedStakeToSpinData));
        this.addResponse("PurchaseEntry", this.updatePurchaseEntryData(parsedStakeToSpinData));
        this.addResponse("Collect", this.updateCollectData(parsedStakeToSpinData));
        const parsedSpinsData = this.parseSpinsData(parsedStakeToSpinData);
        this.addResponse("Spin", this.updateSpinData(parsedSpinsData));
        this.addResponse("StakeToSpin", this.updateStakeToSpinData(parsedSpinsData));
    }
    updateCurrentGameData(data) {
        const currentGameData = this.cloneData(data);
        const spinResults = currentGameData.getDataArray(SlingoConstants.PROPERTY_SPIN_RESULTS);
        if (spinResults && spinResults.length) {
            const progressData = spinResults[0]?.getDataObject(SlingoConstants.PROPERTY_GAME_PROGRESS);
            progressData.putString(SlingoConstants.PROPERTY_COMPLETE_REASON, null);
            progressData.putNumber(SlingoConstants.NEXT_SPIN_PRICE, 0);
            progressData.putNumberArray(SlingoConstants.PROPERTY_MATCHED_NUMBERS, []);
            progressData.putNumber(SlingoConstants.PROPERTY_SPINS_REMAINING, this.getTotalSpinCount(data));
            progressData.putNumber(SlingoConstants.PROPERTY_FREE_SPINS_REMAINING, 0);
            currentGameData.putPlainObject(SlingoConstants.PROPERTY_GAME_PROGRESS, progressData.toObject());
            const actionsData = spinResults[0]?.getDataObject(SlingoConstants.PROPERTY_ACTIONS);
            currentGameData.putPlainObject(SlingoConstants.PROPERTY_ACTIONS, actionsData.toObject());
        }
        else {
            throw new Error("Replay CurrentGameData ERROR");
        }
        currentGameData.putString(SlingoConstants.PROPERTY_STATE, GameState.PURCHASE_ENTRY.getType());
        return currentGameData.toObject();
    }
    updatePurchaseEntryData(data) {
        //take progress data from the first spin result to construct the purchase entry response
        const purchaseEntryData = this.cloneData(data);
        const spinResults = purchaseEntryData.getDataArray(SlingoConstants.PROPERTY_SPIN_RESULTS);
        if (spinResults && spinResults.length) {
            const progressData = spinResults[0]?.getDataObject(SlingoConstants.PROPERTY_GAME_PROGRESS);
            progressData.putString(SlingoConstants.PROPERTY_COMPLETE_REASON, null);
            progressData.putNumber(SlingoConstants.NEXT_SPIN_PRICE, 0);
            progressData.putNumberArray(SlingoConstants.PROPERTY_MATCHED_NUMBERS, []);
            progressData.putNumber(SlingoConstants.PROPERTY_SPINS_REMAINING, this.getTotalSpinCount(data));
            progressData.putNumber(SlingoConstants.PROPERTY_FREE_SPINS_REMAINING, 0);
            purchaseEntryData.putDataObject(SlingoConstants.PROPERTY_GAME_PROGRESS, progressData);
            const actionsData = spinResults[0]?.getDataObject(SlingoConstants.PROPERTY_ACTIONS);
            purchaseEntryData.putPlainObject(SlingoConstants.PROPERTY_ACTIONS, actionsData.toObject());
        }
        else {
            throw new Error("Replay PurchaseEntryData ERROR");
        }
        purchaseEntryData.putString(SlingoConstants.PROPERTY_STATE, GameState.STANDARD_SPIN.getType());
        return purchaseEntryData.toObject();
    }
    updateCollectData(data) {
        //Take progress and feature data from the last spin result to construct the collect response
        const collectData = this.cloneData(data);
        const spinResults = collectData.getDataArray(SlingoConstants.PROPERTY_SPIN_RESULTS);
        if (spinResults && spinResults.length) {
            const lastSpinData = this.cloneData(spinResults[spinResults.length - 1]);
            const spinResultData = lastSpinData.getDataObject(SlingoConstants.PROPERTY_SPIN_RESULT);
            const progressData = lastSpinData.getDataObject(SlingoConstants.PROPERTY_GAME_PROGRESS);
            const featureData = lastSpinData.getDataObject(SlingoConstants.PROPERTY_FEATURE);
            const actionsData = lastSpinData.getDataObject(SlingoConstants.PROPERTY_ACTIONS);
            collectData.putPlainObject(SlingoConstants.PROPERTY_GAME_PROGRESS, progressData.toObject());
            collectData.putPlainObject(SlingoConstants.PROPERTY_ACTIONS, actionsData.toObject());
            if (featureData) {
                collectData.putDataObject(SlingoConstants.PROPERTY_FEATURE, featureData);
            }
            if (spinResultData) {
                collectData.putPlainObject(SlingoConstants.PROPERTY_SPIN_RESULT, spinResultData.toObject());
            }
        }
        else {
            throw new Error("Replay CollectData ERROR");
        }
        collectData.putString(SlingoConstants.PROPERTY_STATE, GameState.COMPLETE.getType());
        return collectData.toObject();
    }
    updateSpinData(parsedData) {
        const spinsWithoutStakeToSpins = parsedData.filter(elem => !this.hasStakeToSpinResult(elem));
        return spinsWithoutStakeToSpins.map(elem => elem.toObject());
    }
    updateStakeToSpinData(parsedData) {
        const spinsWithStakeToSpins = parsedData.filter(elem => this.hasStakeToSpinResult(elem));
        return spinsWithStakeToSpins.map(elem => elem.toObject());
    }
    hasStakeToSpinResult(spinData) {
        const spinResult = spinData.getDataObject(SlingoConstants.PROPERTY_SPIN_RESULT);
        const nextStakeToSpin = spinResult.getDataObject(SlingoConstants.PROPERTY_NEXT_STAKE_TO_SPIN);
        const stakeToSpin = spinResult.getDataObject(SlingoConstants.PROPERTY_STAKE_TO_SPIN);
        return Boolean(nextStakeToSpin || stakeToSpin);
    }
    parseStakeToSpins(data) {
        const replayData = this.cloneData(data);
        const spinResultsArray = replayData.getDataArray(SlingoConstants.PROPERTY_SPIN_RESULTS);
        const firstNextStakeToSpinIndex = spinResultsArray.findIndex(spinData => {
            const spinResult = spinData.getDataObject("spinResult");
            return spinResult?.getDataArray("nextStakeToSpin");
        });
        if (firstNextStakeToSpinIndex === -1) {
            //no stake to spins, return unmodified data
            return data;
        }
        let wheelSpins = 0;
        const totalStaked = spinResultsArray[firstNextStakeToSpinIndex].getDataObject(SlingoConstants.PROPERTY_GAME_PROGRESS).getNumber(SlingoConstants.PROPERTY_TOTAL_STAKED);
        const wheelSpinPrice = spinResultsArray[firstNextStakeToSpinIndex].getDataObject(SlingoConstants.PROPERTY_GAME_PROGRESS).getNumber(SlingoConstants.PROPERTY_NEXT_STAKE_TO_SPIN);
        for (let spinIndex = firstNextStakeToSpinIndex; spinIndex < spinResultsArray.length; spinIndex++) {
            const spin = spinResultsArray[spinIndex];
            const spinGameProgress = spin.getDataObject(SlingoConstants.PROPERTY_GAME_PROGRESS);
            const spinResult = spin.getDataObject(SlingoConstants.PROPERTY_SPIN_RESULT);
            const stakeToSpin = spinResult.getDataObject(SlingoConstants.PROPERTY_STAKE_TO_SPIN);
            const nextStakeToSpinsFound = spinResult.getDataArray(SlingoConstants.PROPERTY_NEXT_STAKE_TO_SPIN) ?? [];
            const losingNextStakeToSpinsFound = nextStakeToSpinsFound.filter(spinResult => !this.isWinningStakeToSpinResult(spinResult));
            spin.putString(SlingoConstants.PROPERTY_STATE, "STANDARD_SPIN");
            spin.putStringArray(SlingoConstants.PROPERTY_ACTIONS, [Action.STAKE_TO_SPIN.getType(), Action.COLLECT.getType()]);
            spinGameProgress.putString(SlingoConstants.PROPERTY_COMPLETE_REASON, null);
            spinResult.putString(SlingoConstants.PROPERTY_SPIN_TYPE, "STANDARD");
            spinResult.putPlainObject(SlingoConstants.PROPERTY_NEXT_STAKE_TO_SPIN, null);
            if (stakeToSpin) {
                // any stakeToSpins found (wether in the raw data, or synthesized from losingNextStakeToSpins previously), represent one spin of the wheel, and need the totalStake calculated.
                wheelSpins++;
                spinGameProgress.putNumber(SlingoConstants.PROPERTY_TOTAL_STAKED, totalStaked + (wheelSpins * wheelSpinPrice));
            }
            if (losingNextStakeToSpinsFound.length > 0) {
                // any losing nextStakeToSpins get split into copies of the associated spin-result, each storing one nextStakeToSpins result in its stakeToSpin object
                losingNextStakeToSpinsFound.forEach((stakeToSpinData, newIndex) => {
                    const newSpinData = this.cloneData(spin);
                    const newSpinDataSpinResult = newSpinData.getDataObject(SlingoConstants.PROPERTY_SPIN_RESULT);
                    newSpinDataSpinResult.putDataObject(SlingoConstants.PROPERTY_STAKE_TO_SPIN, stakeToSpinData);
                    spinResultsArray.splice(spinIndex + 1 + newIndex, 0, newSpinData);
                });
            }
        }
        replayData.putDataArray(SlingoConstants.PROPERTY_SPIN_RESULTS, spinResultsArray);
        return replayData;
    }
    parseSpinsData(data) {
        //Combine the top level getData response with each element of the spinResults array to generate each spin response
        const sharedData = new DataObject();
        sharedData.putDataArray(SlingoConstants.PROPERTY_BALANCE, data.getDataArray(SlingoConstants.PROPERTY_BALANCE));
        sharedData.putNumber(SolidifyableData.RESPONSE_PARAMETER, 0);
        if (data.containsKey(SlingoConstants.PROPERTY_WRAPPER)) {
            sharedData.putPlainObject(SlingoConstants.PROPERTY_WRAPPER, data.getDataObject(SlingoConstants.PROPERTY_WRAPPER));
        }
        else {
            sharedData.putPlainObject(SlingoConstants.PROPERTY_WRAPPER, {});
        }
        if (data.containsKey(SlingoConstants.PROPERTY_GAME_INSTANCE_ID)) {
            sharedData.putNumber(SlingoConstants.PROPERTY_GAME_INSTANCE_ID, data.getNumber(SlingoConstants.PROPERTY_GAME_INSTANCE_ID));
        }
        if (data.containsKey(SlingoConstants.PROPERTY_TICKET)) {
            sharedData.putPlainObject(SlingoConstants.PROPERTY_TICKET, data.getDataObject(SlingoConstants.PROPERTY_TICKET));
        }
        sharedData.putPlainObject(SlingoConstants.PROPERTY_CHANCE_GAMES, null);
        sharedData.putPlainObject(SlingoConstants.PROPERTY_SLOTS_GAME, null);
        sharedData.putPlainObject(SlingoConstants.PROPERTY_SLOTS_SPINS, null);
        this.additionalSharedSpinProperties.forEach(property => {
            if (data.containsKey(property)) {
                sharedData.putPlainObject(property, data.getDataObject(property));
            }
        });
        const spinDataArray = this.parseSpinResultData(data.getDataArray(SlingoConstants.PROPERTY_SPIN_RESULTS), sharedData);
        const lastSpinData = spinDataArray[spinDataArray.length - 1];
        if (data.containsKey(SlingoConstants.PROPERTY_CHANCE_GAMES)) {
            lastSpinData.putPlainObject(SlingoConstants.PROPERTY_CHANCE_GAMES, data.getDataObject(SlingoConstants.PROPERTY_CHANCE_GAMES).toObject());
        }
        if (data.containsKey(SlingoConstants.PROPERTY_SLOTS_GAME)) {
            lastSpinData.putPlainObject(SlingoConstants.PROPERTY_SLOTS_GAME, data.getDataObject(SlingoConstants.PROPERTY_SLOTS_GAME).toObject());
        }
        if (data.containsKey(SlingoConstants.PROPERTY_SLOTS_SPINS)) {
            lastSpinData.putPlainObject(SlingoConstants.PROPERTY_SLOTS_SPINS, data.getDataObject(SlingoConstants.PROPERTY_SLOTS_SPINS).toObject());
        }
        return spinDataArray;
    }
    parseSpinResultData(spinResultsData, sharedData) {
        return spinResultsData?.map((uniqueData, index) => {
            const totalSpins = spinResultsData.length;
            const spinNumber = index + 1;
            const spinData = this.cloneData(sharedData);
            const spinResultData = uniqueData.getDataObject(SlingoConstants.PROPERTY_SPIN_RESULT);
            const gameProgressData = uniqueData.getDataObject(SlingoConstants.PROPERTY_GAME_PROGRESS);
            const actionsData = uniqueData.getDataObject(SlingoConstants.PROPERTY_ACTIONS);
            spinData.putPlainObject(SlingoConstants.PROPERTY_SPIN_RESULT, spinResultData.toObject());
            spinData.putPlainObject(SlingoConstants.PROPERTY_GAME_PROGRESS, gameProgressData.toObject());
            spinData.putPlainObject(SlingoConstants.PROPERTY_ACTIONS, actionsData.toObject());
            this.additionalUniqueSpinProperties.forEach(property => {
                if (uniqueData.containsKey(property)) {
                    uniqueData.putPlainObject(property, uniqueData.getDataObject(property).toObject());
                }
            });
            //Modify spins remaining as if all spins were standard spins. This allows the spin counter to count down to zero through the replay
            spinData.putString(SlingoConstants.PROPERTY_STATE, GameState.STANDARD_SPIN.getType());
            gameProgressData.putNumber(SlingoConstants.PROPERTY_STANDARD_SPINS, totalSpins);
            gameProgressData.putNumber(SlingoConstants.PROPERTY_STANDARD_SPINS_USED, spinNumber);
            gameProgressData.putNumber(SlingoConstants.PROPERTY_SPINS_REMAINING, totalSpins - spinNumber);
            gameProgressData.putNumber(SlingoConstants.PROPERTY_PURCHASABLE_SPINS_AVAILABLE, 0);
            gameProgressData.putNumber(SlingoConstants.PROPERTY_PURCHASE_SPINS_USED, 0);
            gameProgressData.putNumber(SlingoConstants.PROPERTY_PURCHASABLE_SPINS_REMAINING, 0);
            gameProgressData.putNumber(SlingoConstants.PROPERTY_IS_FIRST_PURCHASE_SPIN, 0);
            gameProgressData.putNumber(SlingoConstants.PROPERTY_FREE_SPINS, 0);
            gameProgressData.putNumber(SlingoConstants.PROPERTY_FREE_SPINS_USED, 0);
            gameProgressData.putNumber(SlingoConstants.PROPERTY_FREE_SPINS_REMAINING, 0);
            return spinData;
        });
    }
    isWinningStakeToSpinResult(stakeToSpinResult) {
        return stakeToSpinResult?.getDataObject(SlingoConstants.PROPERTY_FEATURE)?.getDataObject(SlingoConstants.PROPERTY_RNG)?.getBool(SlingoConstants.PROPERTY_IS_WIN);
    }
    getTotalSpinCount(data) {
        return data.getDataArray(SlingoConstants.PROPERTY_SPIN_RESULTS)?.length || 0;
    }
}
//# sourceMappingURL=ReplayParser.js.map